import React, { useState, useEffect } from 'react';
import { Target, Users, CheckCircle2, Clock, TrendingUp, Plus, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { db } from '../../../lib/db';
import { useGoals } from '../../../hooks/useDatabase';
import { formatDate } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

export default function SupervisorGoalsPage() {
  const { user } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const { createGoal, updateGoal, deleteGoal, loading: goalLoading } = useGoals();
  const [teamMembers, setTeamMembers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [myGoals, setMyGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('team');
  const [newGoal, setNewGoal] = useState({ title: '', description: '', priority: 'medium', dueDate: '' });
  const [editingGoalId, setEditingGoalId] = useState(null);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    try {
      const [usersData, goalsData] = await Promise.all([
        db.users.toArray(),
        db.goals.toArray()
      ]);
      const team = usersData.filter(u => u.supervisorId === user?.id && u.status === 'active');
      setTeamMembers(team);
      // Team goals
      const teamGoals = goalsData.filter(g => team.some(m => m.id === g.userId));
      setGoals(teamGoals);
      // My personal goals
      const supervisorGoals = goalsData.filter(g => g.userId === user?.id);
      setMyGoals(supervisorGoals);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGoalsByUser = (userId) => goals.filter(g => g.userId === userId);
  const getUserById = (userId) => teamMembers.find(u => u.id === userId);

  const allTeamGoals = goals;
  const stats = {
    total: allTeamGoals.length,
    completed: allTeamGoals.filter(g => g.status === 'completed').length,
    inProgress: allTeamGoals.filter(g => g.status === 'in_progress').length
  };

  const myStats = {
    total: myGoals.length,
    completed: myGoals.filter(g => g.status === 'completed').length,
    inProgress: myGoals.filter(g => g.status === 'in_progress').length
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'blocked': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-rose-100 text-rose-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleSaveGoal = async () => {
    if (!newGoal.title) {
      toast.error('Please enter a goal title');
      return;
    }
    const dueDateValue = newGoal.dueDate ? new Date(newGoal.dueDate).getTime() : Date.now() + 30 * 24 * 60 * 60 * 1000;
    try {
      if (editingGoalId) {
        await updateGoal(editingGoalId, {
          title: newGoal.title,
          description: newGoal.description,
          priority: newGoal.priority,
          dueDate: dueDateValue,
          updatedAt: Date.now()
        });
        toast.success('Goal updated successfully!');
      } else {
        await createGoal({
          userId: user.id,
          title: newGoal.title,
          description: newGoal.description,
          priority: newGoal.priority,
          dueDate: dueDateValue,
          status: 'not_started',
          type: 'current'
        });
        toast.success('Goal added successfully!');
      }
      setShowAddModal(false);
      setNewGoal({ title: '', description: '', priority: 'medium', dueDate: '' });
      setEditingGoalId(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const openEditGoal = (goal) => {
    const dueDateInput = goal.dueDate ? new Date(goal.dueDate).toISOString().slice(0, 10) : '';
    setNewGoal({
      title: goal.title || '',
      description: goal.description || '',
      priority: goal.priority || 'medium',
      dueDate: dueDateInput
    });
    setEditingGoalId(goal.id);
    setShowAddModal(true);
  };

  const handleDeleteGoal = async (goalId) => {
    const confirmed = await confirm({
      title: 'Delete Goal',
      description: 'Are you sure you want to delete this goal?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      try {
        await deleteGoal(goalId);
        toast.success('Goal deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete goal');
      }
    }
  };

  const handleUpdateGoalStatus = async (goalId, newStatus) => {
    try {
      await updateGoal(goalId, { status: newStatus });
      toast.success('Goal updated!');
      loadData();
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
          <p className="text-gray-500 mt-1">Manage your goals and monitor team progress</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Personal Goal
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="team" className="data-[state=active]:bg-white"><Users className="w-4 h-4 mr-2" /> Team Goals</TabsTrigger>
          <TabsTrigger value="personal" className="data-[state=active]:bg-white"><Target className="w-4 h-4 mr-2" /> My Goals</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-violet-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{stats.total}</p><p className="text-xs text-gray-500">Total Goals</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p><p className="text-xs text-gray-500">In Progress</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{stats.completed}</p><p className="text-xs text-gray-500">Completed</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-teal-600" /></div>
                  <div><p className="text-2xl font-bold text-teal-600">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</p><p className="text-xs text-gray-500">Completion</p></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {teamMembers.map(member => {
            const memberGoals = getGoalsByUser(member.id);
            if (memberGoals.length === 0) return null;
            return (
              <Card key={member.id} className="border-0 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                      {member.profile?.firstName?.[0]}{member.profile?.lastName?.[0]}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{member.profile?.firstName} {member.profile?.lastName}</CardTitle>
                      <p className="text-sm text-gray-500">{member.profile?.position}</p>
                    </div>
                    <Badge variant="secondary" className="ml-auto">{memberGoals.length} goals</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {memberGoals.map(goal => (
                      <div key={goal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Target className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{goal.title}</p>
                            <p className="text-xs text-gray-500">Due: {formatDate(goal.dueDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getPriorityColor(goal.priority)}>{goal.priority}</Badge>
                          <Badge className={getStatusColor(goal.status)}>{goal.status?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {allTeamGoals.length === 0 && (
            <div className="text-center py-12"><Target className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No team goals found</p></div>
          )}
        </TabsContent>

        <TabsContent value="personal" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-violet-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{myStats.total}</p><p className="text-xs text-gray-500">My Goals</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{myStats.inProgress}</p><p className="text-xs text-gray-500">In Progress</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="text-2xl font-bold text-gray-900">{myStats.completed}</p><p className="text-xs text-gray-500">Completed</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-teal-600" /></div>
                  <div><p className="text-2xl font-bold text-teal-600">{myStats.total > 0 ? Math.round((myStats.completed / myStats.total) * 100) : 0}%</p><p className="text-xs text-gray-500">Completion</p></div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {myGoals.map(goal => (
              <Card key={goal.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Target className="w-5 h-5 text-violet-500 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{goal.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge className={getStatusColor(goal.status)}>{goal.status?.replace('_', ' ')}</Badge>
                        <Badge className={getPriorityColor(goal.priority)}>{goal.priority}</Badge>
                                <span className="text-xs text-gray-400">Due: {formatDate(goal.dueDate)}</span>
                                <span className="text-xs text-gray-400">Created: {formatDate(goal.createdAt || goal.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        {goal.status !== 'completed' && (
                          <>
                            {goal.status !== 'in_progress' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateGoalStatus(goal.id, 'in_progress')}>Start</Button>
                            )}
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleUpdateGoalStatus(goal.id, 'completed')}>Complete</Button>
                          </>
                        )}
                                <Button size="sm" variant="ghost" onClick={() => openEditGoal(goal)}><Edit className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => handleDeleteGoal(goal.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {myGoals.length === 0 && (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No personal goals yet</p>
              <Button variant="link" onClick={() => setShowAddModal(true)} className="text-violet-600">Add your first goal</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) { setEditingGoalId(null); setNewGoal({ title: '', description: '', priority: 'medium', dueDate: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGoalId ? 'Edit Personal Goal' : 'Add Personal Goal'}</DialogTitle>
            <DialogDescription>{editingGoalId ? 'Update this goal' : 'Create a new goal for yourself'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input placeholder="Goal title" value={newGoal.title} onChange={(e) => setNewGoal({...newGoal, title: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe your goal..." value={newGoal.description} onChange={(e) => setNewGoal({...newGoal, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newGoal.priority} onValueChange={(v) => setNewGoal({...newGoal, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={newGoal.dueDate} onChange={(e) => setNewGoal({...newGoal, dueDate: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveGoal} disabled={goalLoading} className="bg-violet-600 hover:bg-violet-700 text-white">
              {goalLoading ? 'Saving...' : editingGoalId ? 'Save Changes' : 'Add Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
